import React from "react";
import styled, { useTheme } from "styled-components";
import { ChevronDown } from "lucide-react";
import {
  BacktestingScoreBreakdown,
  LanguageNameSpaces,
  MarketplaceSubGroup,
} from "@shared/types";
import useScore from "@shared/features/newMarketplace/hooks/useScore";
import { ScoreSection } from "@shared/features/newMarketplace/types";
import {
  SCORE_HELPER_PROPS,
  SCORE_SECTION_TO_KEYS,
} from "@shared/features/newMarketplace/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { SCORE_BREAKDOWN_ID, SCORE_SECTION_ID, SCORE_METRIC_ID } from "testIds";
import { ScoreRing } from "features/newMarketplace/NewLook/NewLookScoreBadge";
import NewLookScoreDetails, {
  getScoreSectionColor,
  getScoreTotals,
  getScoreVerdict,
  SCORE_SECTION_LABELS,
} from "features/newMarketplace/NewLook/NewLookScoreDetails";

interface Props {
  group?: MarketplaceSubGroup;
  loading?: boolean;
  hideTitle?: boolean;
  /** The full breakdown (gauge, metrics open) for wide panels. */
  detailed?: boolean;
}

const SECTION_LABELS = SCORE_SECTION_LABELS;

const clampPercent = (value?: number) =>
  `${Math.min(100, Math.max(0, value || 0))}%`;

/**
 * A channel's score: the total as the lead figure, then its three parts as
 * a hairline list; each part opens to its metrics, and metrics scored per
 * period open to those periods.
 */
const NewLookScoreInfo = ({ detailed, ...props }: Props) =>
  detailed ? (
    <NewLookScoreDetails {...props} />
  ) : (
    <CompactScoreInfo {...props} />
  );

const CompactScoreInfo = ({ group, loading, hideTitle }: Props) => {
  const theme = useTheme();
  const score = useScore({ group });
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { totalScore, totalWeight } = getScoreTotals(group, score);
  const verdict = getScoreVerdict(totalScore / Math.max(1, totalWeight));

  if (loading) {
    return (
      <Content aria-busy={true}>
        <Skeleton width={160} height={40} />
        <Skeleton width="100%" height={180} radius={12} />
      </Content>
    );
  }

  const metric = (
    key: keyof BacktestingScoreBreakdown,
    section: ScoreSection
  ) => {
    const data = score.getProgressBarData(key, section);
    return (
      <Metric key={key} data-testid={SCORE_METRIC_ID}>
        <MetricHeading>
          <MetricTitle>
            {data.title}
            {data.helperProps && <HelperTooltip {...data.helperProps} />}
          </MetricTitle>
          <Points>
            <strong>{data.weightedScore}</strong> / {data.weight}
          </Points>
        </MetricHeading>
        {data.isCollapsible ? (
          <Timeframes>
            <summary>
              {t("score.timeframes")}
              <ChevronDown size={14} strokeWidth={1.75} aria-hidden={true} />
            </summary>
            <TimeframeRows>
              {score
                .getTimeframeScoreData(key, section)
                .map(({ timeframe, value, weightedScore, weight }) => (
                  <MetricHeading key={timeframe}>
                    <MetricValue>
                      {t("score.last_days", {
                        timeframe,
                        value: score.getFormattedValue(key, value),
                      })}
                    </MetricValue>
                    <Points>
                      {weightedScore} / {weight}
                    </Points>
                  </MetricHeading>
                ))}
            </TimeframeRows>
          </Timeframes>
        ) : (
          <MetricValue>{data.value}</MetricValue>
        )}
        <Meter $small={true}>
          <span
            style={{
              width: clampPercent(data.progressPercent),
              background: getScoreSectionColor(theme, section),
            }}
          />
        </Meter>
      </Metric>
    );
  };

  return (
    <Content data-testid={SCORE_BREAKDOWN_ID}>
      {!hideTitle && <Heading>{t("score.title")}</Heading>}
      <Overview>
        <ScoreRing
          score={totalScore / Math.max(1, totalWeight)}
          label={String(totalScore)}
          size={88}
          decorative={true}
        />
        <OverviewText>
          {group?.name && <GroupName>{group.name}</GroupName>}
          <Verdict $tone={verdict.tone}>{t(verdict.key)}</Verdict>
          <Total>
            <span>
              {totalScore} / {totalWeight}
            </span>
            <HelperTooltip
              {...SCORE_HELPER_PROPS}
              title={t(SCORE_HELPER_PROPS.title)}
              description={t(SCORE_HELPER_PROPS.description)}
            />
          </Total>
        </OverviewText>
      </Overview>
      <Sections>
        {score.sections.map((section) => {
          const data = score.getSectionRowData(section);
          return (
            <Section
              key={`${group?.group_id}-${section}`}
              data-testid={SCORE_SECTION_ID}
            >
              <summary>
                <ScoreRing
                  score={(data.progressPercent || 0) / 100}
                  label={String(data.weightedScore)}
                  size={40}
                  color={getScoreSectionColor(theme, section)}
                  decorative={true}
                />
                <SectionName>{t(SECTION_LABELS[section])}</SectionName>
                <Points>
                  <strong>{data.weightedScore}</strong> / {data.weight}
                </Points>
                <ChevronDown size={16} strokeWidth={1.75} aria-hidden={true} />
              </summary>
              <Metrics>
                {SCORE_SECTION_TO_KEYS[section].map((key) =>
                  metric(key, section)
                )}
              </Metrics>
            </Section>
          );
        })}
      </Sections>
    </Content>
  );
};

export default NewLookScoreInfo;

const Content = styled.div`
  display: grid;
  gap: 16px;
  min-width: 0;
  text-align: left;
`;
// The total on a soft brand-tinted panel: the modal's one hero.
const Overview = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
  padding: 18px;
  border-radius: 12px;
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  background: ${(p) => getNewLookTokens(p.theme).panel};
`;
const Verdict = styled.span<{ $tone: "up" | "accent" | "stop" | "down" }>`
  align-self: flex-start;
  margin-top: 4px;
  padding: 2px 10px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 12.5px;
  line-height: 18px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme)[p.$tone]};
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const OverviewText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Heading = styled.h3`
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const GroupName = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow-wrap: anywhere;
`;
const Total = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Meter = styled.div<{ $small?: boolean }>`
  height: ${(p) => (p.$small ? 4 : 6)}px;
  border-radius: 3px;
  overflow: hidden;
  background: ${(p) => getNewLookTokens(p.theme).hairline};
  span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(
      90deg,
      ${(p) => p.theme.colors.primaryBlue}99,
      ${(p) => p.theme.colors.primaryBlue}
    );
  }
`;
// The three parts as plain rows on hairlines (no box around them).
const Sections = styled.div`
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  margin: 0 -4px;
`;
const Section = styled.details`
  summary {
    list-style: none;
    cursor: pointer;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  > summary {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) auto 16px;
    align-items: center;
    gap: 12px;
    padding: 10px 4px;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    color: ${(p) => getNewLookTokens(p.theme).muted};
    transition: background-color 120ms;
    ${Meter} {
      grid-column: 1 / -1;
    }
    > svg {
      transition: transform 150ms;
    }
    &:hover {
      background: ${(p) => getNewLookTokens(p.theme).rowHover};
    }
    &:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
    }
  }
  &[open] > summary {
    > svg {
      transform: rotate(180deg);
    }
  }
`;
const SectionName = styled.span`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  overflow-wrap: anywhere;
`;
const Points = styled.span`
  white-space: nowrap;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  strong {
    ${newLookType.figure};
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;
const Metrics = styled.div`
  padding: 0 16px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  ${Meter} {
    margin-top: 4px;
  }
`;
const MetricHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  > :first-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;
const MetricTitle = styled.span`
  display: inline-flex;
  align-items: center;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const MetricValue = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Timeframes = styled.details`
  summary {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 24px;
    border-radius: 6px;
    ${newLookType.meta};
    color: ${(p) => getNewLookTokens(p.theme).muted};
    cursor: pointer;
    &:hover {
      color: ${(p) => getNewLookTokens(p.theme).accent};
    }
    > svg {
      transition: transform 150ms;
    }
    &:focus-visible {
      outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
      outline-offset: 1px;
    }
  }
  &[open] > summary svg {
    transform: rotate(180deg);
  }
`;
const TimeframeRows = styled.div`
  display: grid;
  gap: 6px;
  padding: 8px 10px;
  margin: 4px 0;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
